import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { isTestUser } from "../../utils/testUser"; // TEMPORÄRER TESTZUGANG
import styles from "./Patientenliste.module.css";

import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import { useKlinischerBefund } from "../../context/KlinischerBefundContext";
import { useFussstatus } from "../../context/FussstatusContext";
import { useKriterien } from "../../context/KriterienContext";
import { useKategorisierung } from "../../context/KategorisierungContext";
import { useFussstatusGrafik } from "../../context/FussstatusGrafikContext";
import { useSchuhversorgung } from "../../context/SchuhversorgungContext";

const SALUTATION_TO_GESCHLECHT = {
  "de609b657f5a473298bab08c6df6d35a": "männlich",
  "e04001f133214426b2c7c2155f2af10a": "weiblich",
  "37e91b8cad6647d187a099eda9aaaf48": "divers",
  "ea5064df7ac24d1e9d6455fecfd31836": "Keine Angabe",
};

const mapSalutationToGeschlecht = (salutationId) =>
  SALUTATION_TO_GESCHLECHT[salutationId] ?? null;

const Patientenliste = () => {
  const navigate = useNavigate();
  const { data, setData } = useAllgemeineDaten();

    // Add all context setters and clear functions
  const { data: AllgemeineDaten, setData: setAllgemeineDaten, clearAllgemeineDaten } = useAllgemeineDaten();
  const { setData: setKlinischerBefund, clearKlinischerBefund } = useKlinischerBefund();
  const { setData: setFussstatus, clearFussstatus } = useFussstatus();
  const { setData: setKriterien, clearKriterien } = useKriterien();
  const { setData: setKategorisierung, clearKategorisierung } = useKategorisierung();
  const { setData: setFussstatusGrafik, clearFussstatusGrafik } = useFussstatusGrafik();
  const { setData: setSchuhversorgung, clearSchuhversorgung } = useSchuhversorgung();

  const [loadingDetails, setLoadingDetails] = useState(false);

  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [shopPatientsError, setShopPatientsError] = useState(null);
  const [systemPatientsError, setSystemPatientsError] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showVersionsDropdown, setShowVersionsDropdown] = useState(false);

  const searchRef = useRef(null);
  const versionsRef = useRef(null);

  // Auto fetch patients
useEffect(() => {
  const fetchPatients = async () => {
    setLoading(true);
    setShopPatientsError(null);
    setSystemPatientsError(null);

    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const jwt = stored?.jwt;
      const swContextToken = stored?.token;
      const testUser = isTestUser(); // TEMPORÄRER TESTZUGANG: no shop session

      if (!jwt || (!swContextToken && !testUser)) {
        setShopPatientsError("Authentifizierung fehlgeschlagen. Bitte neu anmelden.");
        setSystemPatientsError("Authentifizierung fehlgeschlagen.");
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${jwt}`,
        "sw-context-token": swContextToken || "",
      };

      const [shopResponse, notShopResponse] = await Promise.allSettled([
        testUser
          ? Promise.resolve(null) // TEMPORÄRER TESTZUGANG: skip shop patients
          : fetch(`${process.env.REACT_APP_API_URL}/api/data/patients`, {
              method: "GET",
              headers,
            }),
        fetch(`${process.env.REACT_APP_API_URL}/api/patientsNotShop?page=1&limit=10000`, {
          method: "GET",
          headers,
        }),
      ]);

      let shopPatients = [];
      let notShopPatients = [];

      if (testUser) {
        // TEMPORÄRER TESTZUGANG: no shop patients, no shop error
      } else if (shopResponse.status === "fulfilled") {
        const shopResult = await shopResponse.value.json();
        if (shopResult.success) {
          shopPatients = shopResult.data || [];
        } else {
          setShopPatientsError("Die Shop-Patienten konnten nicht geladen werden. Etwas ist schiefgelaufen. Bitte versuchen Sie, sich erneut anzumelden.");
        }
      } else {
        setShopPatientsError("Die Shop-Patienten konnten nicht geladen werden. Etwas ist schiefgelaufen. Bitte versuchen Sie, sich erneut anzumelden.");
      }

      if (notShopResponse.status === "fulfilled") {
        const notShopResult = await notShopResponse.value.json();
        if (notShopResult.success) {
          notShopPatients = notShopResult.data || [];
        } else {
          setSystemPatientsError("Die Patienten aus dem System konnten nicht geladen werden.");
        }
      } else {
        setSystemPatientsError("Die Patienten aus diesem System konnten nicht geladen werden.");
      }

      const normalizePatient = (patient, source) => ({
        patientId: patient.patientId || patient.patient_encode || patient.shop_patient_id || "",
        firstName: patient.firstName || patient.vorname || "",
        lastName: patient.lastName || patient.name || "",
        birthday: patient.birthday || patient.geburtsdatum || "",
        salutationId: patient.salutationId || "",
        source,
      });

      const mergedPatients = [
        ...shopPatients.map((patient) => normalizePatient(patient, "shop")),
        ...notShopPatients.map((patient) => normalizePatient(patient, "notshop")),
      ];

      const uniquePatients = Array.from(
        new Map(
          mergedPatients.map((p) => [p.patientId, p])
        ).values()
      );

      setPatients(uniquePatients);
    } catch (err) {
        setShopPatientsError("Die Shop-Patienten konnten nicht geladen werden. Etwas ist schiefgelaufen. Bitte versuchen Sie, sich erneut anzumelden.");
        setSystemPatientsError("Die Patienten aus diesem System konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  fetchPatients();
}, []);

  // Fetch full patient medical data
const fetchPatientDetails = async (lookupId, source) => {
  if (!lookupId) return;
  
  setLoadingDetails(true);
  
  try {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    const jwt = stored?.jwt;
    const swContextToken = stored?.token;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/patients/${lookupId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          "sw-context-token": swContextToken,
        },
      }
    );

    const result = await response.json();

    if (result.success && result.data) {
      const md = result.data;
      const effectiveShopId = md.shop_patient_id || (source === "shop" ? lookupId : "");
      const isShopPatient   = !!effectiveShopId;
      const geschlechtFromSalutation = mapSalutationToGeschlecht(
        md.salutationId || md.general_data?.salutationId
      );

      setData((prev) => ({
        ...prev,
        ...md.general_data,
        barcode: md.patient_encode,
        shopPatientId: effectiveShopId,
        shopListePatient: isShopPatient,
        notShop: !isShopPatient,
        ...(geschlechtFromSalutation !== null && { geschlecht: geschlechtFromSalutation }),
      }));

      setKlinischerBefund(md.clinical_findings || {});
      setFussstatus(md.foot_status || {});
      setFussstatusGrafik(md.foot_status_graphic || {});
      setKriterien(md.criteria || {});
      setKategorisierung(md.categorization || {});
      setSchuhversorgung(md.shoe_provision || {});
    }
  } catch (err) {
    console.error("Failed to load patient details:", err);
  } finally {
    setLoadingDetails(false);
  }
};


  const fetchPatientVersions = async (patientId, restoreVersionId = null) => {
    if (!patientId) return;
    setLoadingVersions(true);
    setVersions([]);
    setSelectedVersion(null);
    setShowVersionsDropdown(false);
    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const jwt = stored?.jwt;
      const swContextToken = stored?.token;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientId}/versions`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
            "sw-context-token": swContextToken,
          },
        }
      );
      const result = await response.json();
      if (result.success) {
        const fetched = result.data || [];
        setVersions(fetched);
        if (fetched.length > 0) {
          const match = restoreVersionId ? fetched.find(v => v.id === restoreVersionId) : null;
          const toSelect = match || fetched[fetched.length - 1];
          setSelectedVersion(toSelect);
          setData(prev => ({ ...prev, selectedVersionId: toSelect.id }));
        }
      }
    } catch (err) {
      console.error("Failed to load versions:", err);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleVersionSelect = async (versionId) => {
    if (!versionId || !selectedPatient) {
      setSelectedVersion(null);
      return;
    }
    const patientId = selectedPatient.patientId;
    setLoadingDetails(true);
    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const jwt = stored?.jwt;
      const swContextToken = stored?.token;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientId}/versions/${versionId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
            "sw-context-token": swContextToken,
          },
        }
      );
      const result = await response.json();
      if (result.success && result.data) {
        const md = result.data;
        const effectiveShopId = md.shop_patient_id || (selectedPatient?.source === "shop" ? patientId : "");
        const isShopPatient   = !!effectiveShopId;
        const geschlechtFromSalutation = mapSalutationToGeschlecht(
          md.salutationId || md.general_data?.salutationId
        );
        setData((prev) => ({
          ...prev,
          ...md.general_data,
          barcode: md.patient_encode,
          shopPatientId: effectiveShopId,
          shopListePatient: isShopPatient,
          notShop: !isShopPatient,
          ...(geschlechtFromSalutation !== null && { geschlecht: geschlechtFromSalutation }),
        }));
        setKlinischerBefund(md.clinical_findings || {});
        setFussstatus(md.foot_status || {});
        setFussstatusGrafik(md.foot_status_graphic || {});
        setKriterien(md.criteria || {});
        setKategorisierung(md.categorization || {});
        setSchuhversorgung(md.shoe_provision || {});
        setSelectedVersion(md);
        setData(prev => ({ ...prev, selectedVersionId: md.id }));
      }
    } catch (err) {
      console.error("Failed to load version data:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Pre-fill from context (even if manually created)
  useEffect(() => {
    // Only pre-fill if we are NOT coming from a list selection
    if (data.name && data.vorname && !selectedPatient) {
      const patientId = data.barcode || "manual";
      const contextPatient = {
        patientId,
        firstName: data.vorname,
        lastName: data.name,
        birthday: data.geburtsdatum || "",
      };
      setSelectedPatient(contextPatient);
      setSearchTerm(`${data.vorname} ${data.name}`);
      fetchPatientVersions(patientId, data.selectedVersionId);
    }
  }, [data.name, data.vorname, data.barcode, data.geburtsdatum, selectedPatient]);

  // Live Search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPatients(patients);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = patients.filter((patient) => {
      const fullName = `${patient.firstName || ""} ${patient.lastName || ""}`.toLowerCase();
      return fullName.includes(term);
    });

    setFilteredPatients(filtered);
  }, [searchTerm, patients]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (versionsRef.current && !versionsRef.current.contains(event.target)) {
        setShowVersionsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (patient) => {
    if (!patient) return;

    const firstName = patient.firstName || patient.vorname || "";
    const lastName  = patient.lastName  || patient.name || "";
    const lookupId  = patient.patientId || patient.patient_encode || patient.shop_patient_id;

    setSelectedPatient(patient);
    setSearchTerm(`${firstName} ${lastName}`.trim());

    const isFromShop = patient.source === "shop";
    const geschlechtFromSalutation = mapSalutationToGeschlecht(patient.salutationId);

    setData((prev) => ({
      ...prev,
      name: lastName,
      vorname: firstName,
      geburtsdatum: patient.birthday || "",
      shopListePatient: isFromShop,
      notShop: patient.source === "notshop",
      shopPatientId: isFromShop ? lookupId : "",
      barcode: lookupId,
      ...(geschlechtFromSalutation !== null && { geschlecht: geschlechtFromSalutation }),
    }));

    fetchPatientDetails(lookupId, patient.source);
    fetchPatientVersions(lookupId);
    setShowDropdown(false);
  };

  const clearSelection = () => {
    setSelectedPatient(null);
    setSearchTerm("");

    setData((prev) => ({
      ...prev,
      barcode: "",
      shopPatientId: "",
      name: "",
      vorname: "",
      geburtsdatum: "",
      shopListePatient: false,
      notShop: false,
      selectedVersionId: null,
    }));

    setVersions([]);
    setSelectedVersion(null);

    // Clear all medical contexts
    clearAllgemeineDaten();
    clearKlinischerBefund();
    clearFussstatus();
    clearFussstatusGrafik();
    clearKategorisierung();
    clearKriterien();
    clearSchuhversorgung();
  };

  return (
    <div className={styles["patientenliste-page"]}>
      <h1>Patientenliste</h1>

      {isTestUser() ? (
        // TEMPORÄRER TESTZUGANG: no shop, so no shop / red-marker explanation
        <p className={styles["info-note"]}>
          Ein Patient kann aus der Liste der in diesem System gespeicherten Patienten ausgewählt werden.
          Falls der Patient nicht in der Liste ist, können Sie einfach auf "Weiter" klicken,
          um einen neuen Patienten anzulegen.
          Zum Entfernen des ausgewählten Patienten klicken Sie auf <strong>✕</strong>.
        </p>
      ) : (
        <p className={styles["info-note"]}>
          Ein Patient kann aus der Shop-Patientenliste ausgewählt werden.
          Patienten mit der roten Markierung sind nur in der Datenbank dieses Systems gespeichert und nicht im Shop.
          Falls der Patient nicht in der Liste ist, können Sie einfach auf "Weiter" klicken,
          um einen neuen Patienten anzulegen.
          Zum Entfernen des ausgewählten Patienten klicken Sie auf <strong>✕</strong>.
        </p>
      )}

      <div className={styles["patientenliste-main"]}>
        {/* Search Input */}
        <div className={styles["search-container"]} ref={searchRef}>
          <div className={styles["search-wrapper"]}>
            <input
              type="text"
              placeholder="Vor- oder Nachname eingeben..."
              className={styles["search-input"]}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              disabled={loading}
            />

            {searchTerm && (
              <button className={styles["clear-btn"]} onClick={clearSelection}>
                ✕
              </button>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && !loading && (
            <div className={styles["dropdown-list"]}>
              {filteredPatients.length > 0 ? (
                filteredPatients.map((patient) => (
                <div
                  key={patient.patientId || patient.patient_encode || patient.shop_patient_id}
                  className={styles["dropdown-item"]}
                  onClick={() => handleSelect(patient)}
                >
                <div className={styles["dropdown-item-content"]}>
                  {patient.source === "notshop" && !isTestUser() /* TEMPORÄRER TESTZUGANG: marker meaningless without shop */ && (
                    <span className={styles["notshop-circle"]}>●</span>
                  )}

                  <div>
                    <strong>{patient.firstName} {patient.lastName}</strong>
                    <span> — {(patient.patientId || patient.patient_encode || patient.shop_patient_id || "—")}</span>
                  </div>
                </div>
                </div>
                ))
              ) : (
                <div className={styles["dropdown-item"]}>Keine Patienten gefunden</div>
              )}
            </div>
          )}
        </div>

        {/* Version history — shown once a patient is selected */}
        {selectedPatient && (
          <div className={styles["versions-section"]}>
            <p className={styles["versions-label"]}>Verfügbare Versionen</p>
            {loadingVersions ? (
              <p className={styles.loading}>Versionen werden geladen...</p>
            ) : versions.length === 0 ? (
              <p className={styles["versions-empty"]}>Keine Versionen vorhanden</p>
            ) : (
              <div className={styles["versions-select-container"]} ref={versionsRef}>
                <div
                  className={styles["versions-select"]}
                  onClick={() => setShowVersionsDropdown((prev) => !prev)}
                >
                  {selectedVersion
                    ? (() => {
                        const date = new Date(selectedVersion.created_at);
                        const formatted = date.toLocaleString("de-DE", {
                          day: "2-digit", month: "2-digit", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        });
                        const actionLabel = selectedVersion.action === "created" ? "erstellt" : "aktualisiert";
                        return `Version ${selectedVersion.version} — ${formatted} (${actionLabel})`;
                      })()
                    : "Version auswählen..."}
                </div>
                {showVersionsDropdown && (
                  <div className={styles["dropdown-list"]}>
                    {versions.map((v) => {
                      const date = new Date(v.created_at);
                      const formatted = date.toLocaleString("de-DE", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      });
                      const actionLabel = v.action === "created" ? "erstellt" : "aktualisiert";
                      return (
                        <div
                          key={v.id}
                          className={styles["dropdown-item"]}
                          onClick={() => {
                            setShowVersionsDropdown(false);
                            handleVersionSelect(v.id);
                          }}
                        >
                          Version {v.version} — {formatted} ({actionLabel})
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && <p className={styles.loading}>Patienten werden geladen...</p>}
        {loadingDetails && <p className={styles.loading}>Patientendetails werden geladen...</p>}
        {shopPatientsError && <p className={styles["error-text"]}>{shopPatientsError}</p>}
        {systemPatientsError && <p className={styles["error-text"]}>{systemPatientsError}</p>}

        <div className={`${styles.row} ${styles.actions}`}>
          <button
            type="button"
            className={styles["btn-primary"]}
            onClick={() => navigate("/start")}
          >
            Zurück
          </button>

          <button
            type="button"
            className={styles["btn-primary"]}
            onClick={() => navigate("/allgemeineDaten")}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
};

export default Patientenliste;